import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Typography,
  Divider,
} from '@mui/material'
import { t } from '@/i18n'
import MenuSection from './MenuSection'
import useIsMobile from '@/hooks/useIsMobile'

const DRAWER_WIDTH = 600
const TABLET_DRAWER_WIDTH = 450
const MOBILE_DRAWER_WIDTH = '100%'

interface MenuSectionProps {
  title: string
  items?: any[]
  sections?: MenuSectionProps[]
  onItemClick?: (path: string, name: string) => void
}

interface PopupMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  sections: MenuSectionProps[]
  onItemClick: (path: string, name: string) => void
  disabled?: boolean
}

const PopupMenu: React.FC<PopupMenuProps> = ({
  anchorEl,
  open,
  onClose,
  sections,
  onItemClick,
  disabled,
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const [selectedSection, setSelectedSection] = useState<string | null>(
    sections[0]?.title || null,
  )

  const handleItemClick = (path: string, name: string) => {
    if (!disabled) {
      onItemClick(path, name)
    }
  }

  const handleSectionClick = (title: string) => {
    setSelectedSection(title)
  }

  const MenuContent = () => (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        width: {
          xs: MOBILE_DRAWER_WIDTH,
          sm: TABLET_DRAWER_WIDTH,
          md: DRAWER_WIDTH,
        },
        maxWidth: {
          xs: MOBILE_DRAWER_WIDTH,
          sm: TABLET_DRAWER_WIDTH,
          md: DRAWER_WIDTH,
        },
        maxHeight: {
          xs: '80vh',
          sm: '90vh',
          md: '80vh',
        },
      }}
    >
      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
          },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontSize: {
              xs: '1.1rem',
              sm: '1.25rem',
            },
          }}
        >
          {t('additional_functions')}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: {
            xs: 1.5,
            sm: 2,
          },
        }}
      >
        {isMobile ? (
          <List>
            {sections.map((section, index) => (
              <MenuSection
                key={index}
                {...section}
                onItemClick={handleItemClick}
              />
            ))}
          </List>
        ) : (
          <Box sx={{ display: 'flex', height: '100%' }}>
            <Box
              sx={{
                width: '250px',
                borderRight: `1px solid ${theme.palette.divider}`,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
              }}
            >
              <List>
                {sections.map(section => (
                  <React.Fragment key={section.title}>
                    <ListItem
                      button
                      selected={selectedSection === section.title}
                      onClick={() => handleSectionClick(section.title)}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        },
                        py: 2,
                      }}
                    >
                      <ListItemText
                        primary={t(section.title)}
                        primaryTypographyProps={{
                          variant: 'subtitle1',
                          fontWeight: selectedSection === section.title ? 600 : 400,
                        }}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            </Box>
            <Box sx={{ flexGrow: 1, p: 2, overflow: 'auto', minWidth: '400px' }}>
              {sections.map(section => (
                <Box
                  key={section.title}
                  sx={{
                    display: selectedSection === section.title ? 'block' : 'none',
                  }}
                >
                  <MenuSection
                    {...section}
                    onItemClick={handleItemClick}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  )

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: MOBILE_DRAWER_WIDTH,
            maxWidth: MOBILE_DRAWER_WIDTH,
            maxHeight: '80vh',
            position: 'fixed',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
            boxShadow: theme.shadows[16],
          },
        }}
        ModalProps={{
          keepMounted: true,
          disablePortal: true,
          style: {
            position: 'absolute',
          },
        }}
      >
        <MenuContent />
      </Drawer>
    )
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[4],
          overflow: 'hidden',
        },
      }}
    >
      <MenuContent />
    </Popover>
  )
}

export default PopupMenu 